import { Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import React, { memo, useContext, useMemo } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import useRoute from '../../../hooks/useRoute';
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from '../../../theme';
import { Dashboard, DashboardQuerySource } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    card: {
        width: 450,
        maxWidth: 450,
        minHeight: 200,
        maxHeight: 400,
        height: 'auto',
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    dealSource: {
        width: '100%',
        height: 30,
        background: theme.colors.blue['100'],
        paddingLeft: 16,
    },
    container: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    masonryElement: {
        width: '100%',
        height: 'fit-content',
        padding: '12px 24px 8px 16px',
        transition: 'all 300ms ease',
    },
    link: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '1rem',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: theme.palette.primary.main,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    email: {
        display: 'inline-block',
        fontFamily: 'Inter !important',
        fontSize: '0.95rem',
        textDecoration: 'none',
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
    },
    source: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sublabel: {
        fontWeight: '400',
        color: theme.colors.neutral['500'],
    },
}));

const DealSourceCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { workspaces } = useContext(WorkspacesContext);
    const { dashboardSource } = useBackwardsCompatible();
    const { redirectToWorkspace } = useRoute();

    const source = useMemo(() => {
        const dealSource = dashboardSource(dashboard);

        return (dealSource?.name !== DashboardQuerySource.LINKEDIN) ? dealSource : undefined;
    }, [dashboard, dashboardSource]);

    const workspace = useMemo(() => workspaces.find(ws =>
        ws?.name?.toLowerCase() === source?.name?.toLowerCase()
        && ws?.email?.toLowerCase() === source?.email?.toLowerCase()
    ), [source, workspaces]);

    return (<>
        <Stack className={classes.masonryElement}>
            <Stack className={classes.container}>
                {!!source?.name && (
                    <Typography className={classes.source}
                        onClick={() => !!workspace ? redirectToWorkspace(workspace?.id) : undefined}
                        sx={{ cursor: !!workspace ? 'pointer' : 'auto'}}>
                        {source?.name}
                    </Typography>
                )}
                {!!source?.email && (
                    <Typography className={classNames(classes.source, classes.sublabel)}>
                        {source?.email}
                    </Typography>
                )}
            </Stack>
            <Stack alignItems="center" justifyContent="center" height={120}>
                <Typography color={theme.colors.neutral['500']} fontSize="0.95rem" fontWeight={500}>
                    {'No description yet.'}
                </Typography>
            </Stack>
        </Stack>
    </>);
};

const DealSourceContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);

    const keyPeople = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:Key People`)
    , [dashboard.id, mappedOverviewQueries]);

    if (!Array.isArray(keyPeople) || keyPeople.length === 0)
        return (<></>);

    return (<>
        <Stack className={classes.card}>
            <Stack className={classes.dealSource} justifyContent="center">
                <Typography fontFamily="Inter" fontSize="0.8rem" fontWeight={700}
                    color={theme.colors.blue['600']}>
                    {'Deal Source'}
                </Typography>
            </Stack>
            <Stack className={classes.container} alignItems="flex-start" justifyContent="stretch">
                <DealSourceCard dashboard={dashboard!} />
            </Stack>
        </Stack>
    </>);
};

export default memo(DealSourceContentCard);
